import { AppRoute, SWAP_URL } from '@/constants/shared';
import { useAuth } from '@/hooks/useAuth';
import { useCompliance } from '@/hooks/useCompliance';
import { Box, Button, Flex, HStack, Separator, Spacer, Spinner, Text, VStack } from '@chakra-ui/react';
import { useAppKit, useAppKitAccount } from '@reown/appkit/react';
import { ReactElement, useRef } from 'react';
import { AiOutlineSwap } from 'react-icons/ai';
import { GoPeople } from 'react-icons/go';
import { PiChartLine } from 'react-icons/pi';
import { TbBuildingBridge2 } from 'react-icons/tb';
import { GoonAvatar } from './goon-avatar';
import { InviteFriendsModal } from './modals/invite-friends-modal';
import { NavButton } from './nav-button';
import { RoundedButton } from './rounded-button';
import { ClipboardIconButton, ClipboardLabel, ClipboardRoot } from './ui/clipboard';
import { PopoverArrow, PopoverBody, PopoverContent, PopoverRoot, PopoverTrigger } from './ui/popover';

type Props = {
  variant?: 'small' | 'default';
  inverted?: boolean;
};

export const ConnectWalletButton = ({ variant = 'default', inverted = false }: Props) => {
  const { address } = useAppKitAccount();
  const { geoCompliance, isPendingGeoCompliance } = useCompliance();
  const { isAuthenticated, isLoading, logout } = useAuth();
  const ref = useRef<HTMLDivElement | null>(null);
  const { open } = useAppKit();
  if (isLoading || isPendingGeoCompliance)
    return (
      <Flex minH={variant === 'small' ? '40px' : '44px'} alignItems="center" justifyContent="center">
        <Spinner />
      </Flex>
    );

  if (!isAuthenticated || !address) {
    return (
      <RoundedButton
        size={variant === 'small' ? 'sm' : 'lg'}
        bgColor={inverted ? 'bg' : 'fg'}
        color={inverted ? 'fg' : 'bg'}
        _hover={{
          bgColor: inverted ? 'bg.muted' : 'fg.muted',
        }}
        onClick={() => {
          open();
        }}
        disabled={geoCompliance?.restrictAccess}
      >
        {variant === 'small' ? 'Connect' : 'Connect Wallet'}
      </RoundedButton>
    );
  }
  return (
    <PopoverRoot initialFocusEl={() => ref.current}>
      <PopoverTrigger asChild>
        <Button variant="subtle" boxSize={variant === 'small' ? 10 : 11} borderRadius="full">
          <GoonAvatar seed={address} />
        </Button>
      </PopoverTrigger>
      <PopoverContent w={280} ref={ref} overflow="auto" pt={2} borderRadius="2xl">
        <PopoverArrow />
        <PopoverBody spaceY={2} justifyContent="space-between" flexFlow="column" display="flex" p={2}>
          {/* Wallet ID */}
          <ClipboardRoot value={address}>
            <HStack justify="space-between" mb={4} pl={2}>
              <ClipboardLabel mb={0}>
                <Box flex={1}>
                  <Text fontWeight="500" fontSize="xs" color="fg.muted">
                    Wallet ID
                  </Text>
                  <Text truncate={true} maxW="180px" fontSize="sm" fontWeight="500">
                    {address}
                  </Text>
                </Box>
              </ClipboardLabel>

              <ClipboardIconButton aria-label="Copy Wallet Address" size="sm" variant="ghost" />
            </HStack>
          </ClipboardRoot>

          {/* Menu Items */}
          <VStack align="stretch" gap={2}>
            <NavItem title="Bridge" icon={<TbBuildingBridge2 />} href={AppRoute.Bridge} />
            <NavItem title="Swap" icon={<AiOutlineSwap />} href={SWAP_URL} />
            <NavItem title="Balance" icon={<PiChartLine />} href={AppRoute.MyProfile} />
            <InviteFriendsModal
              triggerOverride={
                <Button px={2} borderRadius="full" width="full" variant="ghost" direction="fwd">
                  <GoPeople />
                  Invite Friends
                </Button>
              }
            />
            <RoundedButton bgColor="bg.subtle" w="full" color="fg" onClick={logout}>
              Disconnect
            </RoundedButton>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </PopoverRoot>
  );
};

const NavItem = ({ title, icon, href }: { title: string; icon: ReactElement; href: string }) => {
  return (
    <>
      <NavButton px={2} borderRadius="none" width="full" variant="ghost" href={href} direction="fwd">
        {icon}
        <Spacer flex={0} />
        {title}
        <Spacer flex={1} />
      </NavButton>
      <Separator />
    </>
  );
};
